import { Stack, Theme, useMediaQuery } from "@mui/material";
import { StaticImageData } from "next/image";
import {
  MotionImage,
  MotionImageProps,
  MotionStack,
  MotionStackProps,
  MotionTypography,
  MotionTypographyProps,
} from "../motion-components";
import {
  H6_1,
  H6_2,
  H6_4,
} from "@/styles/theme/components/typography.fontvariant";
import {
  bottomToToptextStagger,
  bottomToToptextStaggerChildren,
} from "../animations";
import Link from "@cc/link";
import { ArrowForwardIos, Star } from "@mui/icons-material";

export interface CaseStudySlideProps {
  img: StaticImageData | string;
  tag?: string;
  title: string;
  subTitle?: string;
  href: string;
  TagTypographyProps?: MotionTypographyProps;
  TitleTypographyProps?: MotionTypographyProps;
  SubTitleTypographyProps?: MotionTypographyProps;
  CardWrapperProps?: MotionStackProps;
  ContentWrapperProps?: MotionStackProps;
  ImageProps?: MotionImageProps["sx"] & {
    width: any;
    height: any;
  };
  mergeTagToContent?: boolean;
  hideSubtitleOnMobile?: boolean;
  mostRead?: boolean;
  hoverContent?: string;
}

export const CaseStudySlide = (props: CaseStudySlideProps) => {
  const {
    img,
    tag,
    title,
    subTitle,
    href,
    TagTypographyProps,
    TitleTypographyProps,
    SubTitleTypographyProps,
    CardWrapperProps,
    ContentWrapperProps,
    ImageProps,
    mergeTagToContent,
    hoverContent,
    hideSubtitleOnMobile,
    mostRead,
  } = props;
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  return (
    <Link href={href} style={{ textDecoration: "none" }}>
      <MotionStack
        sx={{
          cursor: "pointer",
          position: "relative",
          "&:after": {
            display: isMobile ? "none" : "flex",
            opacity: 0,
            content: `'${hoverContent || "VIEW CASE STUDY"}'`,
            fontSize: H6_2,
            color: "white",
            width: ImageProps ? ImageProps.width : 1,
            height: ImageProps
              ? ImageProps.height === "auto"
                ? 1
                : ImageProps.height
              : 310,
            position: "absolute",
            top: 0,
            left: 0,
            background:
              "linear-gradient(0deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.70) 100%)",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            transition: "all .3s",
          },
          "&:hover": {
            "&:after": {
              opacity: 1,
            },
          },
        }}
        rowGap={2}
        variants={bottomToToptextStagger(0.4)}
        initial={"initial"}
        whileInView={"animate"}
        viewport={{ once: true }}
        {...CardWrapperProps}
      >
        {mostRead && (
          <Stack
            direction={"row"}
            bgcolor={"rgba(0, 0, 0, 0.60)"}
            p={1.5}
            gap={1.5}
            alignItems={"center"}
            fontSize={H6_4}
            width="max-content"
            position={"absolute"}
            top={0}
            left={0}
            zIndex={1}
          >
            <Star
              sx={{ width: { xs: 16, md: 20 }, height: { xs: 16, md: 20 } }}
            />{" "}
            Most Read
          </Stack>
        )}
        <MotionImage
          src={img}
          alt="childrens"
          width={643}
          height={310}
          sx={{
            position: "relative",
            width: { xs: 310, md: 643 },
            height: { xs: 240, md: 310 },
            objectFit: "cover",
            ...ImageProps,
          }}
          variants={bottomToToptextStaggerChildren}
        />
        {tag && !mergeTagToContent && (
          <MotionTypography
            variant={"caption"}
            color={"grey.A400"}
            p={1.3}
            bgcolor={"#DADADA"}
            width={"max-content"}
            variants={bottomToToptextStaggerChildren}
            {...TagTypographyProps}
          >
            {tag}
          </MotionTypography>
        )}
        <MotionStack
          rowGap={2}
          variants={bottomToToptextStaggerChildren}
          {...ContentWrapperProps}
        >
          {mergeTagToContent && (
            <MotionTypography variant={"body2"} {...TagTypographyProps}>
              {tag}
            </MotionTypography>
          )}
          <MotionTypography
            variant="body1"
            fontSize={H6_1}
            {...TitleTypographyProps}
          >
            {title}
          </MotionTypography>
          {!(hideSubtitleOnMobile && isMobile && !subTitle) && (
            <MotionTypography variant="body1" {...SubTitleTypographyProps}>
              {subTitle}
            </MotionTypography>
          )}
          {isMobile && (
            <Link
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "18px",
                textDecoration: "none",
              }}
              href={href}
            >
              {hoverContent || "View Case Study"}
              <ArrowForwardIos sx={{ ml: "8px" }} fontSize="small" />
            </Link>
          )}
        </MotionStack>
      </MotionStack>
    </Link>
  );
};
